import React from "react";
import "./styles/Terms.css";

const Terms = () => {
  return (
    <div className="terms-container">
      <div className="terms-wrapper">
        <div className="terms-header">
          <h1>Terms and Conditions</h1>
          <p className="terms-last-updated">Last Updated: February 12, 2026</p>
        </div>

        <div className="terms-intro">
          <p>
            Welcome to ExploreEase. By accessing or using our travel management
            platform, you agree to be bound by these Terms and Conditions.
            Please read them carefully before using our services.
          </p>
        </div>

        <div className="terms-toc">
          <h3>Table of Contents</h3>
          <ol>
            <li>
              <a href="#acceptance">Acceptance of Terms</a>
            </li>
            <li>
              <a href="#services">Description of Services</a>
            </li>
            <li>
              <a href="#accounts">User Accounts and Registration</a>
            </li>
            <li>
              <a href="#subscription">Subscription and Payments</a>
            </li>
            <li>
              <a href="#user-content">User Content and Data</a>
            </li>
            <li>
              <a href="#intellectual-property">Intellectual Property Rights</a>
            </li>
            <li>
              <a href="#prohibited-uses">Prohibited Uses</a>
            </li>
            <li>
              <a href="#privacy">Privacy and Data Protection</a>
            </li>
            <li>
              <a href="#disclaimers">Disclaimers and Limitations</a>
            </li>
            <li>
              <a href="#termination">Termination</a>
            </li>
            <li>
              <a href="#changes">Changes to Terms</a>
            </li>
            <li>
              <a href="#contact">Contact Information</a>
            </li>
          </ol>
        </div>

        <div className="terms-content">
          <section id="acceptance" className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, accessing, or using ExploreEase (the
              "Service"), you acknowledge that you have read, understood, and
              agree to be bound by these Terms and Conditions ("Terms"). If you
              do not agree to these Terms, you may not access or use the
              Service.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and
              ExploreEase ("Company," "we," "us," or "our"). You must be at
              least 18 years old or the age of majority in your jurisdiction to
              use our Service.
            </p>
          </section>

          <section id="services" className="terms-section">
            <h2>2. Description of Services</h2>
            <p>
              ExploreEase is a comprehensive travel management platform designed
              to help users organize, track, and enhance their travel
              experiences after booking. Our services include:
            </p>
            <ul>
              <li>Trip organization and management tools</li>
              <li>AI-powered smart packing assistant</li>
              <li>Budget tracking and expense splitting</li>
              <li>Travel journal with photo uploads and mood tracking</li>
              <li>Currency converter and translator tools</li>
              <li>Personalized travel insights and recommendations</li>
              <li>Curated travel playbooks and itineraries</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any
              feature or aspect of the Service at any time without prior notice.
            </p>
          </section>

          <section id="accounts" className="terms-section">
            <h2>3. User Accounts and Registration</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To access certain features of ExploreEase, you must create an
              account by providing accurate, current, and complete information.
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities that occur under your
              account.
            </p>
            <h3>3.2 Account Security</h3>
            <p>
              You agree to immediately notify us of any unauthorized use of your
              account or any other breach of security. We cannot and will not be
              liable for any loss or damage arising from your failure to comply
              with these security obligations.
            </p>
            <h3>3.3 Account Accuracy</h3>
            <p>
              You agree to provide accurate and truthful information when
              creating your account and to keep your information updated.
              Providing false information may result in account termination.
            </p>
          </section>

          <section id="subscription" className="terms-section">
            <h2>4. Subscription and Payments</h2>
            <h3>4.1 Free and Pro Plans</h3>
            <p>
              ExploreEase offers both Free and Pro subscription tiers. The Free
              plan allows users to create one trip with full features. The Pro
              plan offers unlimited trips and access to all premium features.
            </p>
            <h3>4.2 Billing</h3>
            <p>
              Pro subscriptions are billed on a monthly or annual basis, as
              selected during signup. All fees are non-refundable except as
              required by law. You authorize us to charge your payment method
              for all fees incurred.
            </p>
            <h3>4.3 Automatic Renewal</h3>
            <p>
              Your subscription will automatically renew at the end of each
              billing period unless you cancel before the renewal date. You may
              cancel your subscription at any time through your account
              settings.
            </p>
            <h3>4.4 Price Changes</h3>
            <p>
              We reserve the right to change our pricing at any time. Price
              changes will be communicated at least 30 days in advance and will
              take effect at your next renewal period.
            </p>
          </section>

          <section id="user-content" className="terms-section">
            <h2>5. User Content and Data</h2>
            <h3>5.1 Your Content</h3>
            <p>
              You retain all rights to the content you upload to ExploreEase,
              including trip information, journal entries, photos, and other
              data ("User Content"). By uploading User Content, you grant us a
              limited license to store, process, and display your content as
              necessary to provide the Service.
            </p>
            <h3>5.2 Content Responsibility</h3>
            <p>
              You are solely responsible for your User Content. You represent
              and warrant that you have all necessary rights to upload your
              content and that it does not violate any laws, infringe upon any
              third-party rights, or violate these Terms.
            </p>
            <h3>5.3 Data Backup</h3>
            <p>
              While we implement reasonable backup procedures, you are
              responsible for maintaining your own backup copies of User
              Content. We are not liable for any loss or corruption of your
              data.
            </p>
          </section>

          <section id="intellectual-property" className="terms-section">
            <h2>6. Intellectual Property Rights</h2>
            <p>
              The Service and its original content, features, and functionality
              are owned by ExploreEase and are protected by international
              copyright, trademark, patent, trade secret, and other intellectual
              property laws.
            </p>
            <p>
              Our trademarks, logos, and service marks displayed on the Service
              may not be used without our prior written consent. All other
              trademarks not owned by us that appear on the Service are the
              property of their respective owners.
            </p>
          </section>

          <section id="prohibited-uses" className="terms-section">
            <h2>7. Prohibited Uses</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Upload malicious code, viruses, or harmful software</li>
              <li>
                Attempt to gain unauthorized access to the Service or other
                accounts
              </li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems (bots, scrapers) without permission</li>
              <li>Impersonate any person or entity</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Reverse engineer or attempt to extract source code</li>
            </ul>
            <p>
              Violation of these prohibited uses may result in immediate
              termination of your account and legal action.
            </p>
          </section>

          <section id="privacy" className="terms-section">
            <h2>8. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection and use of
              personal information is governed by our Privacy Policy, which is
              incorporated into these Terms by reference. By using the Service,
              you consent to the collection and use of your information as
              described in our Privacy Policy.
            </p>
            <p>
              We implement industry-standard security measures to protect your
              data. However, no method of transmission over the Internet is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section id="disclaimers" className="terms-section">
            <h2>9. Disclaimers and Limitations of Liability</h2>
            <h3>9.1 Service "As Is"</h3>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
              WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
              NON-INFRINGEMENT.
            </p>
            <h3>9.2 No Travel Liability</h3>
            <p>
              ExploreEase is a trip management platform and does not book
              travel, accommodations, or activities. We are not responsible for
              any travel-related issues, cancellations, delays, or losses. All
              travel bookings are made through third-party providers.
            </p>
            <h3>9.3 AI-Generated Content</h3>
            <p>
              Our AI-powered features provide suggestions and recommendations
              based on algorithms. These are for informational purposes only and
              should not be relied upon as professional advice. We are not
              liable for decisions made based on AI-generated content.
            </p>
            <h3>9.4 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, EXPLOREEASE SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
              GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section id="termination" className="terms-section">
            <h2>10. Termination</h2>
            <h3>10.1 Termination by You</h3>
            <p>
              You may terminate your account at any time through your account
              settings or by contacting us at info@explorease.com. Upon
              termination, your right to use the Service will immediately cease.
            </p>
            <h3>10.2 Termination by Us</h3>
            <p>
              We reserve the right to suspend or terminate your account at any
              time, with or without notice, for conduct that we believe violates
              these Terms, is harmful to other users, or is otherwise
              inappropriate.
            </p>
            <h3>10.3 Effect of Termination</h3>
            <p>
              Upon termination, all licenses and rights granted to you will
              immediately cease. We may delete your account and User Content,
              though we may retain certain information as required by law or for
              legitimate business purposes.
            </p>
          </section>

          <section id="changes" className="terms-section">
            <h2>11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will
              notify users of material changes via email or through a notice on
              the Service at least 30 days before the changes take effect. Your
              continued use of the Service after changes become effective
              constitutes acceptance of the revised Terms.
            </p>
            <p>
              We encourage you to review these Terms periodically. The "Last
              Updated" date at the top of this page indicates when these Terms
              were last revised.
            </p>
          </section>

          <section id="contact" className="terms-section">
            <h2>12. Contact Information</h2>
            <p>
              If you have any questions, concerns, or feedback regarding these
              Terms and Conditions, please contact us:
            </p>
            <div className="contact-info-box">
              <p>
                <strong>ExploreEase</strong>
              </p>
              <p>
                Email:{" "}
                <a href="mailto:info@explorease.com">info@explorease.com</a>
              </p>
              <p>Support Hours: Monday – Friday, 9:00 AM – 5:00 PM EST</p>
            </div>
          </section>
        </div>

        <div className="terms-footer">
          <p>
            By using ExploreEase, you acknowledge that you have read,
            understood, and agree to be bound by these Terms and Conditions.
          </p>
          <p className="terms-copyright">
            © 2026 ExploreEase. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
